/**
 * Image Service
 *
 * Service for managing photo URLs and caching for the BiteWise healthcare provider platform.
 * Handles fetching and caching of user profile photos with efficient batch operations.
 *
 * @module services/ImageService
 */

import { apiService } from './ApiService';
import type { GetPhotoUrlsRequest, GetPhotoUrlsResponse } from './domains';
import type { Referral } from './domains/ReferralDomain';

/**
 * Image cache entry interface
 */
interface ImageCacheEntry {
  url: string | null;
  timestamp: number;
  expiresAt: number;
}

/**
 * Image Service Configuration
 */
interface ImageServiceConfig {
  /** Cache duration in milliseconds (default: 5 minutes) */
  cacheDuration?: number;
  /** Maximum number of user IDs to fetch in a single request (default: 100) */
  batchSize?: number;
}

/**
 * Image Service Class
 *
 * Provides efficient photo URL management with caching and batch fetching capabilities.
 * Automatically handles role-based access control and missing photos.
 */
export class ImageService {
  private cache = new Map<string, ImageCacheEntry>();
  private config: Required<ImageServiceConfig>;

  constructor(config: ImageServiceConfig = {}) {
    this.config = {
      cacheDuration: config.cacheDuration ?? 5 * 60 * 1000, // 5 minutes
      batchSize: config.batchSize ?? 100,
    };
  }

  /**
   * Get photo URLs for multiple user IDs with caching
   *
   * @param userIds Array of user IDs to fetch photos for
   * @returns Promise<Record<string, string | null>> Map of user IDs to photo URLs
   */
  async getPhotoUrls(userIds: string[]): Promise<Record<string, string | null>> {
    if (userIds.length === 0) {
      return {};
    }

    // Remove duplicates and filter out invalid IDs
    const uniqueUserIds = [...new Set(userIds)].filter(id => this.isValidUuid(id));

    if (uniqueUserIds.length === 0) {
      return {};
    }

    const now = Date.now();
    const result: Record<string, string | null> = {};
    const idsToFetch: string[] = [];

    // Check cache for existing entries
    for (const userId of uniqueUserIds) {
      const cached = this.cache.get(userId);

      if (cached && cached.expiresAt > now) {
        // Use cached entry
        result[userId] = cached.url;
      } else {
        // Need to fetch
        idsToFetch.push(userId);
      }
    }

    // Fetch missing entries in batches
    if (idsToFetch.length > 0) {
      const fetchedUrls = await this.fetchPhotoUrlsBatch(idsToFetch);

      // Update cache and result
      for (const [userId, url] of Object.entries(fetchedUrls)) {
        this.setCacheEntry(userId, url, now);
        result[userId] = url;
      }
    }

    return result;
  }

  /**
   * Get photo URL for a single user ID
   *
   * @param userId User ID to fetch photo for
   * @returns Promise<string | null> Photo URL or null if not available
   */
  async getPhotoUrl(userId: string): Promise<string | null> {
    const result = await this.getPhotoUrls([userId]);
    return result[userId] ?? null;
  }

  /**
   * Extract unique user IDs from referrals
   *
   * @param referrals Array of referral objects
   * @returns Array of unique user IDs from all referral fields
   */
  extractUserIdsFromReferrals(referrals: Referral[]): string[] {
    const userIds = new Set<string>();

    for (const referral of referrals) {
      // Add all relevant user IDs from referral
      if (referral.referring_physician_id) {
        userIds.add(referral.referring_physician_id);
      }
      if (referral.dietitian_id) {
        userIds.add(referral.dietitian_id);
      }
      if (referral.patient_id) {
        userIds.add(referral.patient_id);
      }
      if (referral.claimed_by_user_id) {
        userIds.add(referral.claimed_by_user_id);
      }
    }

    return Array.from(userIds).filter(id => this.isValidUuid(id));
  }

  /**
   * Preload photo URLs for referrals
   *
   * @param referrals Array of referral objects
   * @returns Promise<Record<string, string | null>> Map of user IDs to photo URLs
   */
  async preloadReferralPhotos(referrals: Referral[]): Promise<Record<string, string | null>> {
    const userIds = this.extractUserIdsFromReferrals(referrals);
    return this.getPhotoUrls(userIds);
  }

  /**
   * Clear cache for specific user IDs
   *
   * @param userIds Array of user IDs to clear from cache
   */
  clearCache(userIds?: string[]): void {
    if (userIds) {
      for (const userId of userIds) {
        this.cache.delete(userId);
      }
    } else {
      this.cache.clear();
    }
  }

  /**
   * Clear expired cache entries
   */
  clearExpiredCache(): void {
    const now = Date.now();

    for (const [userId, entry] of this.cache.entries()) {
      if (entry.expiresAt <= now) {
        this.cache.delete(userId);
      }
    }
  }

  /**
   * Get cache statistics
   *
   * @returns Cache statistics object
   */
  getCacheStats(): {
    totalEntries: number;
    expiredEntries: number;
    validEntries: number;
  } {
    const now = Date.now();
    let expiredEntries = 0;
    let validEntries = 0;

    for (const entry of this.cache.values()) {
      if (entry.expiresAt <= now) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    }

    return {
      totalEntries: this.cache.size,
      expiredEntries,
      validEntries,
    };
  }

  /**
   * Fetch photo URLs in batches
   */
  private async fetchPhotoUrlsBatch(userIds: string[]): Promise<Record<string, string | null>> {
    const result: Record<string, string | null> = {};

    // Process in batches to respect API limits
    for (let i = 0; i < userIds.length; i += this.config.batchSize) {
      const batch = userIds.slice(i, i + this.config.batchSize);

      try {
        const request: GetPhotoUrlsRequest = { user_ids: batch };
        const response: GetPhotoUrlsResponse = await apiService.photo.getPhotoUrls(request);

        // Merge batch results
        Object.assign(result, response.photo_urls);

        // Add null entries for IDs that weren't returned (access control)
        for (const userId of batch) {
          if (!(userId in response.photo_urls)) {
            result[userId] = null;
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch photo URLs for batch:`, batch, error);

        // Set all batch IDs to null on error
        for (const userId of batch) {
          result[userId] = null;
        }
      }
    }

    return result;
  }

  /**
   * Set cache entry with expiration
   */
  private setCacheEntry(userId: string, url: string | null, timestamp: number): void {
    this.cache.set(userId, {
      url,
      timestamp,
      expiresAt: timestamp + this.config.cacheDuration,
    });
  }

  /**
   * Validate UUID format
   */
  private isValidUuid(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }
}

/**
 * Default ImageService singleton instance
 */
export const imageService = new ImageService();